import type {ReactElement} from 'react'

type Cell<TRow extends object> = (a: CellProps<TRow>) => ReactElement | null

export type CellProps<TRow extends object> = {
  cell: {
    row: {
      original: TRow
    }
  }
  row: unknown
}

type Order = 'asc' | 'desc'

export type Column<TRow extends object> = {
  // How to render the header. Note, of course, `string` or `null` are both
  // valid React Node
  Header?: ReactElement | string
  // How to "get" the column's cell value. This is used for sorting (e.g. when
  // `disableSortBy` is specified) or rendering if `Cell` is not specified.
  accessor?: (originalRow: TRow, rowIndex?: number) => unknown
  // Special rendering component for cells.
  //  - `cell.row.original` is the original, intact passed data row
  //  - `cell.value` is the result of `accessor`
  //
  // There are many more values passed here that we can make accessible if need
  // be.
  Cell?: Cell<TRow>
  // What the component will call this header/cell when referring to it
  // programatically.
  id?: string
  // How to horizontally align headers and cells.
  alignment?: Alignment
  // Attach a className to the cell's `td` element, derived from the row.
  tdClassName?: (a: TRow) => string
  // Attach a className to the cell's `th` element, derived from the row.
  thClassName?: () => string
  // Specify `true` to disallow users from sort by clicking on this column.
  disableSortBy?: true
  // Which direction, if any, should we initially sort the table by for this
  // column. This can be specified whether or not `disableSortBy` is given.
  defaultSortOrder?: Order
  // Force the column to have a specific width, in pixels.
  widthPx?: number
  // The display label of a parent, grouping column.
  headerGroup?: string
  columns?: Column<TRow>[]
}

export type Alignment = 'left' | 'center' | 'right'

export function aligned(alignment: Alignment): {alignment: Alignment} {
  return {alignment}
}

export const disableSortBy = true as const

export function idAccessorFromProp<TRow>(prop: keyof TRow): {
  accessor: (originalRow: TRow) => unknown
  id: string
} {
  return {
    accessor: originalRow => originalRow[prop],
    id: prop.toString()
  }
}

export function columnWhen<TRow extends object>(
  condition: boolean,
  column: Column<TRow>
): Column<TRow>[] {
  return condition ? [column] : []
}

export function cellFromRow<TRow extends object>(
  mkComponent: (a: TRow) => ReactElement | null
): Cell<TRow> {
  function Cell(props: CellProps<TRow>) {
    const original = props.cell.row.original
    return mkComponent(original)
  }

  return Cell
}
